/**
 * Enterprise teams (T1): defined in AIOE from catalogue roles and skills, with human members, and
 * granted to projects and compute pools (node tags). A project's workbench loads the teams granted
 * to it as the `organisation` team scope, read-only.
 *
 *   GET|POST /portfolio/v1/teams, PUT|DELETE /portfolio/v1/teams/:id   portfolio managers and administrators,
 *                                                                from the console or their desktop
 *   GET  /portfolio/v1/me                                        { admin, portfolioManager, manages[] }: what the caller may do
 *   GET  /projects/v1/:id/teams                                 members: the granted teams as TeamConfig documents
 *
 * `GET /projects/v1/:id/teams` answers the workbench's own `TeamConfig` shape
 * (mju-ai-workbench src/acp/teamConfig.ts): `{ id, kind, name, version, members[] }`, each member
 * `{ name, avatar, color, role, agentInstructions, human?, githubLogin?, provider?, model?, runsOn? }`.
 * A member's instructions are the role's catalogue content followed by its skills'.
 */
import { z } from "zod";
import { teamPlacementSchema } from "./projects.js";
const teamIdSchema = z.string().min(1).max(100).regex(/^[a-z0-9][a-z0-9-]*$/, "lower-case letters, digits and -");
export const orgTeamMemberSchema = z.object({
    name: z.string().min(1).max(100),
    /** A catalogue entry of kind `role`: what this member is and how it works. */
    roleId: z.string().min(1).max(100).optional(),
    /** Catalogue entries of kind `skill` this member carries. */
    skillIds: z.array(z.string().min(1).max(100)).max(50).default([]),
    /** A person rather than an agent: the operations manager assigns them tasks, nothing is spawned. */
    human: z.boolean().default(false),
    /** For a human member: their Entra object id. */
    subjectId: z.string().min(1).max(200).optional(),
    githubLogin: z.string().min(1).max(100).optional(),
    /** Agent runtime and model, in the workbench's terms ("claude-code", "copilot", …). */
    provider: z.string().min(1).max(40).optional(),
    model: z.string().min(1).max(200).optional(),
    runsOn: teamPlacementSchema.optional(),
    /** An agent member's Entra agent identity (object id in the customer's tenant), set by the AIOE's identity broker (Agent 365 plan, A2/A6). */
    entraAgentId: z.string().regex(/^[0-9a-fA-F-]{8,64}$/).optional(),
    /** The person accountable for that identity (its Entra sponsor), by name or email. */
    sponsor: z.string().min(1).max(200).optional(),
});
export const orgTeamGrantsSchema = z.object({
    /** Projects whose members' workbenches load this team. */
    projects: z.array(z.string().min(1).max(200)).max(500).default([]),
    /** Compute pools: node tags the team's agents may run on (a member's own `runsOn.tags` wins). */
    pools: z.array(z.string().min(1).max(64)).max(50).default([]),
});
export const orgTeamSchema = z.object({
    id: teamIdSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    /** The member who leads (the workbench's `orchestration.opsManager`). */
    lead: z.string().max(100).optional(),
    members: z.array(orgTeamMemberSchema).min(1).max(50),
    grants: orgTeamGrantsSchema,
    /** Bumped on every save; the workbench reloads a team when it changes. */
    version: z.number().int().nonnegative(),
    updatedAt: z.string(),
    updatedBy: z.string(),
});
export const orgTeamPutSchema = orgTeamSchema.omit({ id: true, version: true, updatedAt: true, updatedBy: true }).extend({
    grants: orgTeamGrantsSchema.default({ projects: [], pools: [] }),
});
export const orgTeamCreateSchema = orgTeamPutSchema.extend({ id: teamIdSchema });
const PALETTE = ["#58a6ff", "#3fb950", "#d29922", "#f778ba", "#a371f7", "#39c5cf", "#ff7b72", "#79c0ff", "#56d364", "#e3b341"];
/**
 * Build the workbench's TeamConfig from an enterprise team and the catalogue it refers to. A role
 * or skill missing from the catalogue is left out of the instructions rather than failing the team.
 */
/** `people`: a label (email, else name) for each human member's subjectId, where the platform knows one. */
export function toTeamConfig(team, catalogue, people = {}) {
    const byId = new Map(catalogue.map((e) => [e.id, e]));
    const text = (c) => (typeof c === "string" ? c : c === undefined ? "" : JSON.stringify(c, null, 2));
    return {
        id: team.id,
        kind: "team",
        name: team.name,
        version: team.version,
        ...(team.lead ? { orchestration: { opsManager: team.lead } } : {}),
        members: team.members.map((m, i) => {
            const role = m.roleId ? byId.get(m.roleId) : undefined;
            const skills = m.skillIds.map((id) => byId.get(id)).filter((e) => Boolean(e));
            const instructions = [role ? text(role.content) : "", ...skills.map((s) => `## Skill: ${s.name}\n\n${text(s.content)}`)].filter(Boolean).join("\n\n");
            const tags = m.runsOn?.tags ?? (team.grants.pools.length ? team.grants.pools : undefined);
            const runsOn = m.runsOn || tags ? { ...(m.runsOn ?? {}), ...(tags ? { tags } : {}) } : undefined;
            return {
                name: m.name,
                avatar: i % 10,
                color: PALETTE[i % PALETTE.length],
                ...(role ? { role: role.name } : {}),
                ...(instructions ? { agentInstructions: instructions } : {}),
                ...(m.human ? { human: true } : {}),
                ...(m.human && m.subjectId ? { subjectId: m.subjectId, ...(people[m.subjectId] ? { personLabel: people[m.subjectId].slice(0, 320) } : {}) } : {}),
                ...(m.githubLogin ? { githubLogin: m.githubLogin } : {}),
                ...(m.provider ? { provider: m.provider } : {}),
                ...(m.model ? { model: m.model } : {}),
                ...(m.entraAgentId && !m.human ? { entraAgentId: m.entraAgentId } : {}),
                ...(m.sponsor && !m.human ? { sponsor: m.sponsor } : {}),
                ...(runsOn && !m.human ? { runsOn } : {}),
            };
        }),
    };
}
//# sourceMappingURL=orgTeams.js.map